import React, { useEffect, useMemo, useState } from "react";
import ConfigProps from "../constants/configProps";
import NavigationTokens from "../constants/navigationTokens";
import { ICartItem } from "../interfaces/ICartItem";
import { ICartTotals } from "../interfaces/ICartTotals";
import { IDisplayState } from "../interfaces/IDisplayState";
import { IItemIndicator } from "../interfaces/IItemIndicator";
import { IUserBudget } from "../interfaces/IUserBudget";
import ConfigPropService from "../utilities/configPropService";
import NavigationTokensService from "../utilities/navigationTokensService";
import QueryStringService from "../utilities/queryStringService";
import Icon from "./icon/icon";
import NotificationsCart from "./noticationsCart";
import SearchComponent from "./search/searchComponent";
import UserPanel from "./userPanel/userPanel";
import WbmLogoWrapper from "./wbmLogo/wbmLogoWrapper";
import { IAlgoliaConfig } from "../interfaces/IAlgoliaConfig";
import useIsMobile from "../hooks/useIsMobile";
import AlgoliaSearchProvider from "./search/algoliaSearchProvider";

export interface IHeaderMainProps {
  safeZoneHeight: number;
  customLogo?: string;
  isLoggedIn: boolean;
  displayState: IDisplayState;
  setNavMenuOpen: (value: boolean) => void;
  navMenuOpen: boolean;
  cartTotals: ICartTotals;
  removeCartItem: (cartItem: ICartItem) => void;
  setNotificationsOpen: (value: boolean) => void;
  notificationCount: number;
  isCookied: boolean;
  itemIndicators: IItemIndicator[];
  userBudget?: IUserBudget;
  firstName: string;
  useNotificationAnimation: boolean;
  hasMultipleAccounts: boolean;
  forceCloseDep: boolean;
  accountNumber?: string;
  customerAccountName?: string;
  searchKeyword?: string;
  searchAction?: string;
  myListsUrl?: string;
  algoliaConfig?: IAlgoliaConfig;
  setMyListsOpen: (value: boolean) => void;
}

const HeaderMain = (props: IHeaderMainProps) => {
  const [searchKeyword, setSearchKeyword] = useState<string>(
    QueryStringService.getParam("Keyword") ||
      QueryStringService.getParam("q") ||
      props.searchKeyword ||
      "",
  );
  const [homeHref, setHomeHref] = useState<string>("");
  const [configProps, setConfigProps] = useState<Record<string, string>>();
  const isMobile = useIsMobile();

  useEffect(() => {
    const navTokenSub = NavigationTokensService.get([
      NavigationTokens.home,
    ]).subscribe((navTokens: Record<string, string>) => {
      setHomeHref(navTokens[NavigationTokens.home] as string);
    });

    const configPropsSub = ConfigPropService.get([
      ConfigProps.SaveUserSearches,
      ConfigProps.EnableTypeAhead,
      ConfigProps.DefaultSortOrderSearch,
      ConfigProps.EnableResponsiveSearchResults,
    ]).subscribe((configProps: Record<string, string>) => {
      setConfigProps(configProps);
    });

    return () => {
      navTokenSub.unsubscribe();
      configPropsSub.unsubscribe();
    };
  }, []);

  const style = useMemo(() => {
    return props.safeZoneHeight > 0 ? { top: props.safeZoneHeight } : null;
  }, [props.safeZoneHeight]);

  const onSearchKeywordChange = (value: string) => {
    setSearchKeyword(value);
  };

  const renderWbmLogo = (isMobile = false) => {
    return (
      <WbmLogoWrapper
        customLogo={props.customLogo}
        isMobile={isMobile}
        href={homeHref}
        isLoggedIn={props.isLoggedIn}
        displayState={props.displayState}
      />
    );
  };

  return (
    <React.Fragment>
      <div className="header-main-mobile d-lg-none">
        <div className="row">
          <div className="col-12 col-padding-modifier main-mobile-top">
            <button
              className="menu-icon-button no-print"
              onClick={() => {
                props.setNavMenuOpen(!props.navMenuOpen);
              }}
            >
              <Icon className="d-inline-block">menu</Icon>
            </button>
            <span
              className="header-mobile-wbm-logo"
              style={style ? { top: style.top + 60 } : undefined}
            >
              {renderWbmLogo(true)}
            </span>
            <NotificationsCart
              mobile
              setNotificationsOpen={props.setNotificationsOpen}
              notificationCount={props.notificationCount}
              accountNumber={props.accountNumber}
              customerAccountName={props.customerAccountName}
              cartNumberOfItems={
                props.cartTotals.items ? props.cartTotals.items.length : 0
              }
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 main-mobile-search col-padding-modifier">
            {isMobile && props.algoliaConfig ? (
              <AlgoliaSearchProvider
                searchAction={props.searchAction}
                algoliaConfig={props.algoliaConfig}
              />
            ) : (
              <SearchComponent
                searchKeyword={searchKeyword}
                onSearchKeywordChange={onSearchKeywordChange}
                displayState={props.displayState}
                isMobile
                configProps={configProps}
                searchAction={props.searchAction}
              />
            )}
          </div>
        </div>
      </div>
      <div className="header-main-desktop d-none d-lg-block row">
        <div
          className={
            "header-desktop-wbm-logo " + (props.customLogo ? "custom" : "")
          }
          style={style ? { top: style.top + 60 } : undefined}
        >
          {renderWbmLogo(false)}
        </div>
        <div
          className="search-container"
          style={style ? { top: style.top + 60 } : undefined}
        >
          {!isMobile && props.algoliaConfig ? (
            <AlgoliaSearchProvider
              searchAction={props.searchAction}
              algoliaConfig={props.algoliaConfig}
            />
          ) : (
            <SearchComponent
              searchKeyword={searchKeyword}
              onSearchKeywordChange={onSearchKeywordChange}
              displayState={props.displayState}
              forceCloseDep={props.forceCloseDep}
              configProps={configProps}
              searchAction={props.searchAction}
            />
          )}
        </div>
        <UserPanel
          style={style ? { top: style.top + 40 } : undefined}
          styleDropdown={style ? { top: style.top + 100 } : undefined}
          firstName={props.firstName}
          isLoggedIn={props.isLoggedIn}
          isCookied={props.isCookied}
          setNotificationsOpen={props.setNotificationsOpen}
          notificationCount={props.notificationCount}
          useNotificationAnimation={props.useNotificationAnimation}
          displayState={props.displayState}
          hasMultipleAccounts={props.hasMultipleAccounts}
          myListsUrl={props.myListsUrl}
          setMyListsOpen={props.setMyListsOpen}
        />
      </div>
    </React.Fragment>
  );
};

export default HeaderMain;
